import { useMemo } from 'react'
import Button from '../../uiV2/common/form/inputs/Button'
import CheckboxInput from '../../uiV2/common/form/inputs/CheckboxInput'
import Modal from '../../uiV2/common/modals/Modal'
import type { WorkHistoryCsSearchResultDto } from '../../api/workHistory/models/WorkHistoryCsDtos'
import type { CompanyCsSearchResultDto } from '../../api/company/models/companyCs/CompanyCsDtos'
import CompanyColdStorageSearch from './CompanyColdStorageSearch'

type Props = {
    companies: WorkHistoryCsSearchResultDto[]
    targetCompany?: CompanyCsSearchResultDto
    onTargetCompanySelect: (company: CompanyCsSearchResultDto) => void
    onTargetCompanyClear?: () => void
    onMerge: () => void | Promise<void>
    mergeLoading: boolean
    canMerge: boolean
    errorMessage?: string
    onClose: () => void
    alwaysMergeSelected: boolean
    onAlwaysMergeChange: (value: boolean) => void
}

export default function WorkHistoryMergePreviewModal({
    companies,
    targetCompany,
    onTargetCompanySelect,
    onTargetCompanyClear,
    onMerge,
    mergeLoading,
    canMerge,
    errorMessage,
    onClose,
    alwaysMergeSelected,
    onAlwaysMergeChange,
}: Props): JSX.Element {
    const previewRows = useMemo(() => {
        return companies.map((company) => (
            <tr key={company.id}>
                <td>{company.companyName || '-'}</td>
                <td className="text-xs text-gray-700">{company.companyId}</td>
                <td className="text-xs text-gray-700">{company.id}</td>
            </tr>
        ))
    }, [companies])

    const targetCompanyName = targetCompany?.name?.trim()

    const alwaysMergeFooter = targetCompany ? (
        <CheckboxInput
            id="always-merge-companies"
            label={
                targetCompanyName
                    ? `Always merge these company names into ${targetCompanyName}`
                    : 'Always merge these company names into the selected company'
            }
            description="Future imports with these company names will automatically merge into the selected company."
            checked={alwaysMergeSelected}
            onChange={onAlwaysMergeChange}
            disabled={!canMerge || mergeLoading}
        />
    ) : undefined

    return (
        <Modal
            title="Merge Companies Preview"
            size="xl"
            onClose={onClose}
            allowUserToClose={!mergeLoading}
            leftFooter={alwaysMergeFooter}
            rightFooter={
                <>
                    <Button
                        type="button"
                        style="secondary"
                        onClick={onClose}
                        enabled={!mergeLoading}
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        style="primary"
                        onClick={() => {
                            void onMerge()
                        }}
                        enabled={canMerge && !mergeLoading}
                    >
                        {mergeLoading ? 'Merging…' : 'Merge'}
                    </Button>
                </>
            }
        >
            <p className="mb-4 text-sm text-gray-600">
                The following companies are ready for merging. Confirm the
                selection below and continue when ready.
            </p>
            <div className="overflow-x-auto">
                <table className="main-table w-full">
                    <thead>
                        <tr>
                            <th>Company</th>
                            <th>Company ID</th>
                            <th>Work History ID</th>
                        </tr>
                    </thead>
                    <tbody>{previewRows}</tbody>
                </table>
            </div>
            <div className="mt-6">
                <h3 className="text-base font-semibold text-gray-800">
                    Select Target Company
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                    Search cold storage for the company that already has a data
                    provider id. This will be the destination for the merge.
                </p>
                {errorMessage && (
                    <div className="mt-3 text-sm text-red-600">
                        {errorMessage}
                    </div>
                )}
                {targetCompany && (
                    <div className="mt-4 rounded border border-sky-200 bg-sky-50 p-4 text-sm text-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium text-gray-900">
                                    {targetCompany.name || 'Untitled company'}
                                </div>
                                <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-600">
                                    <span>Company ID: {targetCompany.id}</span>
                                    {targetCompany.dataProviderId && (
                                        <span>
                                            Data Provider ID:{' '}
                                            {targetCompany.dataProviderId}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {onTargetCompanyClear && (
                                <Button
                                    type="button"
                                    style="secondary"
                                    size="small"
                                    onClick={onTargetCompanyClear}
                                    enabled={!mergeLoading}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>
                )}
                <div className="mt-4">
                    <CompanyColdStorageSearch
                        onSelect={onTargetCompanySelect}
                        selectedCompanyId={targetCompany?.id}
                        enforcedCompanyEntityFilter="hasDataProviderId"
                        sectionSubtitle="Search Company Cold Storage"
                    />
                </div>
            </div>
        </Modal>
    )
}
